import {
  Calendar,
  Clock,
  ControlSlider,
  EditPencil,
  Folder,
  Keyframes,
  Settings,
  User
} from 'iconoir-react';

import capitalizeFirstLetter from '~/common/helpers/capitalizeFirstLetter';
import { parseCronExpression } from '~/common/helpers/cronValidation';
import i18n from '~/common/helpers/i18n';
import { IconoirIcon } from '~/common/types/Icon';
import { Deployment } from '~/entities/Deployment';

export type DeploymentInfo = {
  icon?: IconoirIcon;
  title?: string;
  titleTooltip?: string | null;
  value?: string | null;
  valueTooltip?: string | null;
};

/** One array per line of the summary, the way the beehive sidesheet grouped them. */
export type DeploymentInfoRows = DeploymentInfo[][];

/**
 * Every field the beehive sidesheet showed above the fold, in the same order and grouped into the
 * same lines. Lives apart from the component so the HeroUI drawer and its tests read the same
 * list — the legacy `PipelineDeployWithServerless` keeps its own copy until the flag is removed
 * and it is deleted with it.
 */
export const buildDeploymentSummaryInfos = (
  deployment: Deployment
): DeploymentInfoRows => {
  const cronExpression =
    deployment?.pipeline?.triggerSpec?.cronExpression || '';
  const { isValidCron, humanReadableCron } =
    parseCronExpression(cronExpression);

  let cronValue = null;
  if (cronExpression) {
    cronValue = isValidCron
      ? humanReadableCron
      : i18n.t('scenes.runtime.labels.invalid_cron_expression');
  }

  const concurrentScheduling =
    deployment?.pipeline?.triggerSpec?.concurrentScheduling;

  let concurrentSchedulingValue = null;
  if (concurrentScheduling !== undefined) {
    concurrentSchedulingValue = concurrentScheduling ? 'on' : 'off';
  }

  return [
    [
      {
        title: i18n.t('label.environment') || '',
        value: capitalizeFirstLetter(
          deployment?.activeConfiguration?.environment?.name || ''
        )
      }
    ],
    [
      {
        icon: Folder,
        title: i18n.t('noun.project') || '',
        value: deployment?.project
      }
    ],
    [
      {
        icon: Calendar,
        // Labelled, unlike the beehive rows: the drawer reads them as label/value pairs, and a
        // bare timestamp beside a bare user name says nothing about which is which.
        title: i18n.t('scenes.runtime.labels.start_time') || '',
        value: deployment?.startTime
          ? new Date(parseInt(deployment.startTime, 10)).toLocaleDateString(
              undefined,
              {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'shortOffset'
              }
            )
          : null
      },
      {
        icon: Settings,
        title: i18n.t('label.engine_version') || '',
        value: deployment?.deploymentStatus?.pipelineEngineVersion?.replace(
          /^([^.]*\.[^.]*\.[^.]*).*/,
          '$1'
        )
      }
    ],
    [
      {
        icon: User,
        title: i18n.t('scenes.runtime.labels.deployed_by') || '',
        value: deployment?.metadata?.userName
      },
      {
        icon: Keyframes,
        // `on`/`off` the way concurrent scheduling below reads, instead of the bare
        // "Multi Instance" the beehive row showed with no label beside it.
        title: i18n.t('label.multi_instance') || '',
        value: deployment?.pipeline?.parameterizedReplica ? 'on' : null
      }
    ],
    [
      {
        icon: EditPencil,
        title: i18n.t('scenes.runtime.labels.event_name') || '',
        value: deployment?.pipeline?.triggerSpec?.eventName
      }
    ],
    [
      {
        icon: ControlSlider,
        title: i18n.t('scenes.runtime.labels.conc_scheduling') || '',
        titleTooltip:
          i18n.t('scenes.runtime.labels.conc_scheduling_tooltip') || '',
        value: concurrentSchedulingValue
      },
      {
        icon: Clock,
        title: i18n.t('scenes.runtime.labels.recurring_intervals') || '',
        value: cronValue,
        valueTooltip: cronValue
      }
    ]
  ];
};
